import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, ModalsProvider } from '@speeki/global-ui-components';

import { Container } from '@components/ions/layouts';
import { Footer } from '@components/molecules/Footer/Footer';
import { LoginText } from '@components/molecules/LoginText/LoginText';
import { DefaultLayout } from '@components/templates/DefaultLayout/DefaultLayout';

import { normalizeRichTextWhitespace } from '@utils/helpers';
import { useTenantByUuid } from '@utils/hooks/useTenantByUuid';

import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import { authVar } from '@boot/reactiveVariables';
import { MobileTiles } from '@domains/dashboard/organisms/MobileTiles/MobileTiles';

import {
    ButtonIconTextElement,
    LoginLink,
    MobileContentWrapper,
    MobileDashboardPageWrapper,
    MobileDashboardTitle,
    MobileLoginActionsWrapper,
    MobileTilesWrapper,
} from './DashboardPage.styled';

interface SmDashboardPageProps {
    modules?: {
        cases: boolean;
        disclosures: boolean;
        surveys: boolean;
    };
    tenantName?: string;
}

export const SmDashboardPage = ({
    modules,
    tenantName,
}: SmDashboardPageProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenantUuid = AuthService.getTenantUuid();

    const { data, loading } = useTenantByUuid({
        skip: !tenantUuid,
        variables: {
            uuid: tenantUuid ?? '',
        },
    });
    const isLoggedIn = useReactiveVar(authVar).isLoggedIn;
    const headline = data?.tenant?.headline;
    const regex = /(<([^>]+)>)/gi; // remove HTML tags
    const clearHeadline = headline
        ?.replace(regex, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

    return (
        <DefaultLayout
            contentPadding={{
                bottom: 0,
                top: 0,
            }}
            hideBackgroundImage
            hideFooter
        >
            <MobileDashboardPageWrapper>
                <MobileContentWrapper>
                    <Container>
                        {isLoggedIn && (
                            <ButtonIconTextElement
                                iconProps={{
                                    icon: 'SvgAddNew',
                                    size: 24,
                                }}
                                isFixed
                                onPress={() =>
                                    !!modules?.surveys || !!modules?.disclosures
                                        ? ModalsProvider.open(
                                              'MobileStartToModal',
                                          )
                                        : navigate(
                                              getPath({
                                                  path: '/new-conversation',
                                              }),
                                          )
                                }
                                testId="c2nWR6r"
                            />
                        )}
                        {!loading && (
                            <MobileDashboardTitle>
                                {clearHeadline?.length ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: normalizeRichTextWhitespace(
                                                headline,
                                            ),
                                        }}
                                    />
                                ) : (
                                    <>
                                        {t('WELCOME_TO')} {tenantName}{' '}
                                        {t('SOLUTION_TO_MANAGE')}
                                    </>
                                )}
                            </MobileDashboardTitle>
                        )}
                        <MobileTilesWrapper>
                            {modules && <MobileTiles modules={modules} />}
                        </MobileTilesWrapper>
                    </Container>
                </MobileContentWrapper>
                {!isLoggedIn && (
                    <MobileLoginActionsWrapper>
                        <LoginLink to={getPath({ path: '/auth/login/guest' })}>
                            <Button
                                fullWidth
                                testId="button_get-started"
                                variant="primary"
                            >
                                {t('GET_STARTED')}
                            </Button>
                        </LoginLink>
                        <LoginText />
                        <Footer />
                    </MobileLoginActionsWrapper>
                )}
            </MobileDashboardPageWrapper>
        </DefaultLayout>
    );
};
